<script setup lang="ts">
import {
  EBadge,
  EButton,
  EEmptyState,
  ESkeleton,
  ETextarea,
  toProblemDetails,
  useToast,
} from '@erpx/ui'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'

import QueryError from '@/components/QueryError.vue'
import { useRightRail } from '@/composables/useRightRail'
import { problemText } from '@/i18n/problem'
import { newIdempotencyKey } from '@/lib/idempotency'
import {
  AI_KEYS,
  confirmPreview,
  discardPreview,
  listConversations,
  listMessages,
  sendMessage,
  citationRoute,
  startConversation,
  type MessageDTO,
} from '@/modules/ai/api'
import { AI_PERMISSIONS } from '@/modules/ai/permissions'
import { useAuthStore } from '@/stores/auth'

import './ai.css'

/**
 * The AI Assistant, in the right rail (design C12 §5.5; CLAUDE.md §11, §12).
 *
 * What this panel is, and — more importantly — what it is not:
 *
 *  - **It never writes.** A write tool's result is a *preview*, and the preview
 *    card below is the only place a human turns one into a document. There is
 *    one `ConfirmPreview` in the product and this panel calls it; the panel has
 *    no other write path and must never grow one.
 *  - **The tool transcript is shown, with each call's name.** An assistant that
 *    reports a figure without saying which tool produced it is asking to be
 *    believed. Every call is listed, and a call that failed says so.
 *  - **Citations are deep links.** A doc number the reader can open is the
 *    difference between an answer and an assertion.
 *  - **It runs under the caller's own JWT and can never exceed their
 *    permissions**, so a tool the user may not call is simply never offered by
 *    the server. Nothing here needs to hide anything the server would allow.
 *
 * Streaming: `Service.SendMessageStream` exists and is tested, but no HTTP
 * route wraps it yet (`internal/ai/handler.go:94-104` explains why — the
 * conformance suite has no schema convention for a `text/event-stream` success
 * response). So the panel posts and waits, and shows a real pending state
 * rather than pretending to stream.
 */

const { t } = useI18n()
const auth = useAuthStore()
const toast = useToast()
const queryClient = useQueryClient()
const rail = useRightRail()

const allowed = computed(() => auth.can(AI_PERMISSIONS.assistantUse))

const conversationId = ref<number | null>(null)
const draft = ref('')

/**
 * A ⌘K command hands its question over here.
 *
 * It lands in the composer and stops. Nothing sends it: a palette entry that
 * fired a model call would be a keystroke the user never read, and §12's whole
 * confirmation posture starts with the user having read what they asked.
 * A seed never overwrites something already typed.
 */
watch(
  () => rail.assistantSeed.value,
  (seed) => {
    if (seed !== '' && draft.value.trim() === '') draft.value = seed
  },
  { immediate: true },
)
const sending = ref(false)
const sendError = ref('')
const busyPreview = ref<number | null>(null)

const { data: conversations, isPending: conversationsPending } = useQuery({
  queryKey: AI_KEYS.conversations,
  queryFn: listConversations,
  enabled: allowed,
  retry: false,
})

const activeId = computed(() => conversationId.value ?? conversations.value?.[0]?.id ?? null)

const {
  data: messages,
  isPending,
  error,
  refetch,
} = useQuery({
  queryKey: computed(() => AI_KEYS.messages(activeId.value ?? 0)),
  queryFn: () => listMessages(activeId.value ?? 0),
  enabled: computed(() => allowed.value && activeId.value !== null),
})

const log = computed<MessageDTO[]>(() => messages.value ?? [])

/**
 * Previews still awaiting a decision, newest first.
 *
 * A tool call carries `previewId` when the tool was a write. The card is
 * rendered from the message that produced it, so a reader sees the preview
 * beside the turn that proposed it rather than in a separate list.
 */
const pendingPreviews = computed(() =>
  log.value
    .flatMap((message) =>
      message.toolCalls
        .filter((call) => call.previewId !== null && call.previewId !== undefined)
        .map((call) => ({ messageId: message.id, name: call.name, previewId: call.previewId ?? 0, summary: call.resultSummary })),
    )
    .reverse(),
)

async function ensureConversation(): Promise<number> {
  const existing = activeId.value
  if (existing !== null) return existing
  const created = await startConversation(newIdempotencyKey())
  await queryClient.invalidateQueries({ queryKey: AI_KEYS.conversations })
  conversationId.value = created.id
  return created.id
}

async function send(): Promise<void> {
  const text = draft.value.trim()
  if (text === '' || sending.value) return
  sending.value = true
  sendError.value = ''
  try {
    const id = await ensureConversation()
    await sendMessage(id, text, newIdempotencyKey())
    draft.value = ''
    await queryClient.invalidateQueries({ queryKey: AI_KEYS.messages(id) })
  } catch (failure) {
    const problem = toProblemDetails(failure)
    sendError.value = problem === null ? problemText(failure) : problemText(problem)
  } finally {
    sending.value = false
  }
}

async function confirm(previewId: number): Promise<void> {
  if (busyPreview.value !== null) return
  busyPreview.value = previewId
  try {
    await confirmPreview(previewId, newIdempotencyKey())
    await queryClient.invalidateQueries({ queryKey: AI_KEYS.messages(activeId.value ?? 0) })
    toast.success(t('ai.assistant.confirmed'), '')
  } catch (failure) {
    const problem = toProblemDetails(failure)
    toast.error(
      t('ai.assistant.confirmFailed'),
      problem === null ? problemText(failure) : problemText(problem),
    )
  } finally {
    busyPreview.value = null
  }
}

async function discard(previewId: number): Promise<void> {
  if (busyPreview.value !== null) return
  busyPreview.value = previewId
  try {
    await discardPreview(previewId, newIdempotencyKey())
    await queryClient.invalidateQueries({ queryKey: AI_KEYS.messages(activeId.value ?? 0) })
    toast.info(t('ai.assistant.discarded'), '')
  } catch (failure) {
    const problem = toProblemDetails(failure)
    toast.error(
      t('ai.assistant.discardFailed'),
      problem === null ? problemText(failure) : problemText(problem),
    )
  } finally {
    busyPreview.value = null
  }
}

const lastMessage = computed(() => log.value[log.value.length - 1] ?? null)
</script>

<template>
  <!-- Unreachable without `ai.assistant.use`. The rail does not offer the tab
       either; this is the second gate, for the same reason the services check
       their own permissions after the route middleware did. -->
  <div v-if="!allowed" class="ai-chat" data-test="assistant-forbidden">
    <EEmptyState
      :title="t('ai.assistant.forbiddenTitle')"
      :description="t('ai.assistant.forbiddenDescription')"
      icon="info"
      compact
    />
  </div>

  <div v-else class="ai-chat" data-test="assistant-panel">
    <QueryError
      v-if="error"
      :error="error"
      :title="t('ai.assistant.loadFailed')"
      @retry="refetch()"
    />

    <ESkeleton v-else-if="conversationsPending || (isPending && activeId !== null)" variant="row" :lines="4" />

    <EEmptyState
      v-else-if="log.length === 0"
      :title="t('ai.assistant.emptyTitle')"
      :description="t('ai.assistant.emptyDescription')"
      icon="command"
      compact
      data-test="assistant-empty"
    />

    <div v-else class="ai-chat__log" data-test="assistant-log">
      <div
        v-for="message in log"
        :key="message.id"
        class="ai-msg"
        :class="`ai-msg--${message.role === 'user' ? 'user' : 'assistant'}`"
        data-test="assistant-message"
      >
        <span class="ai-msg__role">{{ t(`ai.assistant.roles.${message.role}`) }}</span>
        <span>{{ message.content }}</span>

        <div v-if="message.toolCalls.length > 0" class="ai-tools" data-test="assistant-tools">
          <div v-for="call in message.toolCalls" :key="call.toolUseId" class="ai-tool">
            <EBadge :tone="call.isError ? 'danger' : 'neutral'" size="sm">{{ call.name }}</EBadge>
            <span>{{ call.resultSummary }}</span>
          </div>
        </div>

        <div v-if="message.citations.length > 0" class="ai-citations" data-test="assistant-citations">
          <template
            v-for="citation in message.citations"
            :key="`${citation.docType}-${citation.docId ?? citation.docNo ?? citation.label}`"
          >
            <RouterLink
              v-if="citationRoute(citation) !== null"
              class="link"
              :to="citationRoute(citation) ?? ''"
            >
              {{ citation.label }}
            </RouterLink>
            <span v-else>{{ citation.label }}</span>
          </template>
        </div>
      </div>
    </div>

    <!-- The one place a model's proposal becomes a document. -->
    <div
      v-for="preview in pendingPreviews"
      :key="preview.previewId"
      class="ai-preview"
      data-test="assistant-preview"
    >
      <strong>{{ t('ai.assistant.previewTitle', { tool: preview.name }) }}</strong>
      <p class="ai-preview__body">{{ preview.summary }}</p>
      <p>{{ t('ai.assistant.previewNote') }}</p>
      <div class="ai-actions">
        <EButton
          variant="primary"
          size="sm"
          :loading="busyPreview === preview.previewId"
          data-test="assistant-confirm"
          @click="confirm(preview.previewId)"
        >
          {{ t('ai.assistant.confirm') }}
        </EButton>
        <EButton
          variant="ghost"
          size="sm"
          :loading="busyPreview === preview.previewId"
          data-test="assistant-discard"
          @click="discard(preview.previewId)"
        >
          {{ t('ai.assistant.discard') }}
        </EButton>
      </div>
    </div>

    <div class="ai-form">
      <p v-if="sendError !== ''" class="ai-form__summary" role="alert">{{ sendError }}</p>
      <ETextarea
        v-model="draft"
        :rows="3"
        :maxlength="4000"
        :placeholder="t('ai.assistant.placeholder')"
        data-test="assistant-input"
      />
      <div class="ai-actions">
        <EButton
          variant="primary"
          size="sm"
          :loading="sending"
          data-test="assistant-send"
          @click="send"
        >
          {{ t('ai.assistant.send') }}
        </EButton>
      </div>
    </div>

    <p v-if="lastMessage !== null" class="ai-footer" data-test="assistant-footer">
      <span v-if="lastMessage.model">{{ lastMessage.model }}</span>
      <span>
        {{
          t('ai.assistant.tokens', {
            input: lastMessage.inputTokens,
            output: lastMessage.outputTokens,
          })
        }}
      </span>
    </p>
  </div>
</template>
